'use client';

import { faChevronLeft, faChevronRight } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { formatText, TextRole } from '@pickleballinc/lib/format-text';
import { Application } from '@pickleballinc/lib/types';
import clsx from 'clsx';
import { ReactNode, useMemo, useState } from 'react';

export interface BallProductCardImage {
	id?: string;
	sortOrder: number;
	urlStandard: string;
}

export interface BallProductCardProduct {
	id: string;
	name: string;
	price?: number | null;
	finalPrice?: number | null;
	url?: string;
	images?: BallProductCardImage[] | null;
}

interface BallProductCardProps {
	application: Application;
	product: BallProductCardProduct;
	title: string;
	shopNowLabel?: string;
	logo?: ReactNode;
	imageRenderer: (props: { key: string | undefined; src: string | undefined; alt: string; className: string; fallbackSrc?: string }) => ReactNode;
	fallbackImageUrl?: string;
	ballIndex: number;
	ballCount: number;
	onPreviousBall: () => void;
	onNextBall: () => void;
	shopButtonClassName?: string;
	showShopLink?: boolean;
	emptyImageLabel?: string;
	emptyImageCaption?: string;
	productDescription?: string;
}

const APP_SHOP_BUTTON_CLASSES: Record<Application, string> = {
	pickleball: 'bg-[#471CB1]',
	tournament: 'bg-[#114BBF]',
	clubs: 'bg-[#B42318]',
	leagues: 'bg-[#F73D68]',
	'team-leagues': 'bg-primary',
	'apr-rankings': 'bg-[#4CA30D]'
};

export const BallProductCard = ({
	application,
	product,
	title,
	shopNowLabel = 'Shop now',
	logo,
	imageRenderer,
	fallbackImageUrl,
	ballIndex,
	ballCount,
	onPreviousBall,
	onNextBall,
	shopButtonClassName,
	showShopLink = true,
	emptyImageLabel,
	emptyImageCaption,
	productDescription
}: BallProductCardProps) => {
	const formattedTitle = formatText({ text: title, role: TextRole.CardTitle });
	const formattedShopNowLabel = formatText({ text: shopNowLabel, role: TextRole.ButtonLabel });
	const formattedEmptyImageLabel = emptyImageLabel ? formatText({ text: emptyImageLabel, role: TextRole.DescriptiveText }) : undefined;
	const formattedEmptyImageCaption = emptyImageCaption ? formatText({ text: emptyImageCaption, role: TextRole.DescriptiveText }) : undefined;
	const formattedProductDescription = productDescription ? formatText({ text: productDescription, role: TextRole.DescriptiveText }) : undefined;
	const images = useMemo(() => [...(product.images ?? [])].sort((a, b) => a.sortOrder - b.sortOrder), [product.images]);
	const [carousel, setCarousel] = useState({ productId: product.id, index: 0 });
	const currentIndex = carousel.productId === product.id ? Math.min(carousel.index, Math.max(images.length - 1, 0)) : 0;
	const hasMultipleImages = images.length > 1;
	const hasMultipleBalls = ballCount > 1;
	const current = images[currentIndex];
	const ballCountLabel = useMemo(
		() => formatText({ text: `Ball ${ballIndex + 1} of ${ballCount}`, role: TextRole.DescriptiveText }),
		[ballIndex, ballCount]
	);
	const hasFinalPrice = typeof product.finalPrice === 'number';
	const isDiscounted = hasFinalPrice && typeof product.price === 'number' && product.price > product.finalPrice!;

	const goTo = (index: number, e: React.MouseEvent) => {
		e.stopPropagation();
		setCarousel({ productId: product.id, index: Math.max(0, Math.min(images.length - 1, index)) });
	};

	return (
		<div className="flex flex-col rounded-2xl border border-gray-200 bg-white pb-2">
			<div className="flex items-center justify-between rounded-t-2xl border-b border-gray-200 bg-white px-4 py-3">
				<span className="text-base font-bold text-gray-900">{formattedTitle}</span>
				{logo}
			</div>

			<div className="relative flex h-[312px] items-center justify-center border-b border-gray-200 bg-white px-2 pt-2">
				{current?.urlStandard || (fallbackImageUrl && !emptyImageLabel) ? (
					imageRenderer({
						key: current?.urlStandard ?? fallbackImageUrl,
						src: current?.urlStandard ?? fallbackImageUrl,
						alt: product.name,
						className: 'size-full object-contain',
						fallbackSrc: fallbackImageUrl
					})
				) : emptyImageLabel ? (
					<div className="flex h-full w-full flex-col items-center justify-center rounded-lg bg-gray-50">
						<span className="text-4xl font-bold text-gray-300">{formattedEmptyImageLabel}</span>
						{formattedEmptyImageCaption ? <span className="mt-1 text-sm text-gray-500">{formattedEmptyImageCaption}</span> : null}
					</div>
				) : null}
				{!!hasMultipleImages && (
					<>
						<span className="absolute right-2 top-2 rounded-full border border-gray-200 bg-gray-50 px-2 py-0.5 text-[10px] font-medium text-gray-700">
							{formatText({ text: `${currentIndex + 1} of ${images.length}`, role: TextRole.DescriptiveText })}
						</span>
						{currentIndex > 0 && (
							<button
								type="button"
								aria-label={formatText({ text: 'Previous image', role: TextRole.ButtonLabel })}
								onClick={(e) => goTo(currentIndex - 1, e)}
								className="absolute left-2 top-1/2 flex size-6 -translate-y-1/2 items-center justify-center text-gray-500 hover:text-gray-700"
							>
								<FontAwesomeIcon icon={faChevronLeft} className="size-3" />
							</button>
						)}
						{currentIndex < images.length - 1 && (
							<button
								type="button"
								aria-label={formatText({ text: 'Next image', role: TextRole.ButtonLabel })}
								onClick={(e) => goTo(currentIndex + 1, e)}
								className="absolute right-2 top-1/2 flex size-6 -translate-y-1/2 items-center justify-center text-gray-500 hover:text-gray-700"
							>
								<FontAwesomeIcon icon={faChevronRight} className="size-3" />
							</button>
						)}
					</>
				)}
			</div>

			<div className={clsx('flex flex-col justify-between px-3 pt-4', productDescription ? 'gap-3 pb-3' : 'h-[100px]')}>
				<div>
					<p className="line-clamp-2 text-sm font-medium text-gray-900">{product.name}</p>
					{formattedProductDescription ? <p className="mt-1 text-sm text-gray-600">{formattedProductDescription}</p> : null}
				</div>
				<div className="flex items-center justify-between">
					<div className="flex flex-col">
						{!!isDiscounted && <span className="text-xs font-normal text-gray-600 line-through">${product.price?.toFixed(2)}</span>}
						{!!hasFinalPrice && <span className="text-sm font-bold text-error-500">${product.finalPrice?.toFixed(2)}</span>}
					</div>
					{showShopLink && product.url ? (
						<a
							href={product.url}
							target="_blank"
							rel="noopener noreferrer"
							className={`rounded-lg px-2.5 py-1 text-xs font-semibold text-white ${shopButtonClassName ?? APP_SHOP_BUTTON_CLASSES[application]}`}
						>
							{formattedShopNowLabel}
						</a>
					) : null}
				</div>
			</div>

			{!!hasMultipleBalls && (
				<div className="mt-2 flex items-center justify-center gap-3 border-t border-gray-200 px-3 pt-2">
					<button
						type="button"
						aria-label={formatText({ text: 'Previous ball', role: TextRole.ButtonLabel })}
						onClick={onPreviousBall}
						className="flex size-6 items-center justify-center rounded-full text-gray-500 hover:text-gray-700"
					>
						<FontAwesomeIcon icon={faChevronLeft} className="size-3" />
					</button>
					<span className="text-xs text-gray-500">{ballCountLabel}</span>
					<button
						type="button"
						aria-label={formatText({ text: 'Next ball', role: TextRole.ButtonLabel })}
						onClick={onNextBall}
						className="flex size-6 items-center justify-center rounded-full text-gray-500 hover:text-gray-700"
					>
						<FontAwesomeIcon icon={faChevronRight} className="size-3" />
					</button>
				</div>
			)}
		</div>
	);
};
